// router.js

import get from "lodash/get";
import compose from "lodash/flowRight";
import { parse, stringify } from "query-string";

import { ASC, DESC } from "constants/sorts";
import {
  flagOnOrDevelopment,
  ROLLOUT_TRACE_LEVEL_ROOT_CAUSE,
} from "components/Flag";
import { withDurationAndEndTime } from "apm/TraceSummary/helpers/trace-summary";
import { idFor } from "utils/id";
import {
  TAB_PARAM_ON_DECK,
  DECK_PARAM,
} from "constants/infrastructureQueryParams";
import { exceptionsTabEnum } from "apm/constants/service";

//TODO -we should clean those const's - taken from "/apm/modules/selected-trace"
export const SELECTION_TYPE_ERROR = "errors";
export const SELECTION_TYPE_QUERY = "queries";
export const SELECTION_TYPE_LAYER = "layer";
export const SELECTION_TYPE_SERVICE = "service";
export const SELECTION_INCLUDE_ALL = "all";
export const SELECTION_INCLUDE_ASYNC = "async";
export const SELECTION_INCLUDE_SYNC = undefined;

export const PERCENTILES = [100, 99, 95, 75, 50];
export const desc = "desc";
export const asc = "asc";
const validDirections = [ASC, DESC];
export const SELECTION_TYPE_PARAM = "selectionType";
export const SELECTION_PARAM = "selection";
export const SELECTION_INCLUDE_PARAM = "selectionInclude";
export const ORIGIN_SPAN = "originSpan";

/**
 * Update a react-router location object.
 *
 * https://github.com/ReactTraining/history#listening
 */
export const updateLocation = (location, pathname, search) => {
  let loc = Object.assign({}, location);
  if (pathname) {
    loc.pathname = pathname;
  }

  if (search) {
    loc = mergeSearchParams(loc, search);
  }

  return loc;
};

export const getLastElementFromPath = path => path.split("/").slice(-1)[0];

export const withoutFocus = location => {
  return removeQueryParams(location, [
    "focus_start",
    "focus_end",
    "focus_min",
    "focus_max",
  ]);
};

export const withoutSort = location => {
  return removeQueryParams(location, ["orderby", "orderBy", "sort"]);
};

export const withoutPercentile = location => {
  return removeQueryParams(location, ["percentile"]);
};

export const withoutLength = location => {
  return removeQueryParams(location, ["length"]);
};

export const withoutPage = location => {
  return removeQueryParams(location, ["page"]);
};

export const withoutHttpStatus = location => {
  return removeQueryParams(location, ["http_status"]);
};

export const withoutSearchParam = location => {
  return removeQueryParams(location, ["search"]);
};

export const withoutTimeShiftParam = location => {
  return removeQueryParams(location, ["time_shift"]);
};

export const withoutDeletedService = location => {
  return removeQueryParams(location, ["deleted_service"]);
};

export const withoutTags = location => removeQueryParams(location, ["tag_set"]);

export const withoutDeck = location =>
  removeQueryParams(location, [DECK_PARAM, TAB_PARAM_ON_DECK]);

export const withoutTableParams = location =>
  compose(withoutLength, withoutSearchParam)(location);

export const withoutSidePanelParams = location =>
  compose(withoutLength, withoutSearchParam, withoutDeck)(location);

export const withDurationOnly = location => {
  const loc = Object.assign({}, location);
  const query = parse(loc.search);
  if (query.duration && query.end_time) {
    loc.search = `?${stringify({
      duration: query.duration,
      end_time: query.end_time,
    })}`;
  } else if (query.duration) {
    loc.search = `?${stringify({
      duration: query.duration,
    })}`;
  } else {
    loc.search = "";
  }

  return loc;
};

export const withoutOriginSpan = location =>
  removeQueryParams(location, ["originSpan"]);

export const withoutSpanMultiSelect = location =>
  removeQueryParams(location, [
    SELECTION_TYPE_PARAM,
    SELECTION_PARAM,
    SELECTION_INCLUDE_PARAM,
  ]);

export const withoutSpanMultiSelectWithOrigin = location =>
  compose(withoutSpanMultiSelect, withoutOriginSpan)(location);

export const withoutSelectionInclude = location =>
  removeQueryParams(location, [SELECTION_INCLUDE_PARAM]);

/* Removes all query parameter filters that are unique to the
 * Service Traced Requests screen.
 */
export const withoutTracedRequestsFilters = location =>
  compose(
    withoutSort,
    withoutFocus,
    withoutPercentile,
    withoutHttpStatus,
    withoutLength,
    withoutPage,
    withoutTimeShiftParam,
    withoutTags
  )(location);

export const mergeSearchParams = (location = {}, search) => {
  return {
    ...location,
    search: `?${stringify({
      ...parse(location.search),
      ...search,
    })}`,
  };
};

/**
 * Set or remove a query parameter on a react-router location object.
 *
 * https://github.com/ReactTraining/history#listening
 */
export const setOrRemoveQueryParam = (location, key, value) => {
  let loc = Object.assign({}, location);
  const query = parse(loc.search);

  if (query[key] == value) {
    delete query[key];
    loc.search = `?${stringify(query)}`;
  } else {
    loc = updateLocation(location, undefined, {
      [key]: value,
    });
  }

  return loc;
};

/**
 * Remove selected query parameters from a react-router location object.
 */
export const removeQueryParams = (location, params) => {
  let loc = Object.assign({}, location);
  const query = parse(loc.search);

  for (let p of params) {
    if (query[p]) {
      delete query[p];
    }
  }

  loc.search = `?${stringify(query)}`;

  return loc;
};

/**
 * Update the sort direction.
 */
export const updateSort = (order, isSortColumn) => {
  const next = {
    desc: asc,
    asc: desc,
  };

  if (isSortColumn) {
    return next[order];
  }

  return desc;
};

export const getNextSortParams = (next, current, direction) => ({
  orderBy: next,
  sort: current === next ? (direction === ASC ? DESC : ASC) : direction,
});

export const getOrderByParams = (location, validSorts, defaults) => {
  const { orderBy: orderByParam, sort: sortParam } = parse(location.search);

  const sort =
    orderByParam && validSorts.includes(orderByParam)
      ? orderByParam
      : defaults.sort;

  const direction =
    sortParam && validDirections.includes(sortParam)
      ? sortParam
      : defaults.direction;

  return { sort, direction };
};

export const getParsedQuery = (state, props) => {
  const location = get(props, "location") || get(state, "routing.location");
  return parse(get(location, "search"));
};

export const getOrderBy = (state, props) =>
  getParsedQuery(state, props).orderby || getParsedQuery(state, props).orderBy;

export const getSanitizedOrderBy = (state, props, validSortAttributes) => {
  const attr = getOrderBy(state, props);
  return attr && validSortAttributes.indexOf(attr) >= 0 ? attr : null;
};

export const getSort = (state, props) => getParsedQuery(state, props).sort;

export const getFilterBy = (state, props) =>
  getParsedQuery(state, props).filterBy;

export const getLength = (state, props) =>
  parseInt(getParsedQuery(state, props).length, 10) || 20;

export const getPage = (state, props) =>
  parseInt(getParsedQuery(state, props).page, 10) || 1;

export const getPercentile = (state, props) =>
  getParsedQuery(state, props).percentile;

export const getDeletedService = (state, props) =>
  getParsedQuery(state, props).deleted_service;

export const getTimeShift = (state, props) =>
  getParsedQuery(state, props).time_shift || props.defaultTimeShift;

export const getEndTime = (state, props) => {
  return getParsedQuery(state, props).end_time;
};

export const getLayoutType = (state, props) => {
  return getParsedQuery(state, props).layout;
};

export const getDeckParams = (state, props) => {
  const deck = JSON.parse(getParsedQuery(state, props).deck)[0];
  return {
    node: deck[0],
    value: deck[1],
  };
};

export const getOriginSpanId = (state, props) => {
  const search = get(props, "location.search");
  const { originSpan } = parse(search);
  return originSpan;
};
const areSearchParamsSet = location => {
  return location.search && location.search !== "?";
};

// Default service path
export const servicePath = (organization, service) => {
  return `/apm/${organization}/services/${service}`;
};

// Service Map
export const linkToMapClosePanel = location => {
  return updateLocation(withoutSidePanelParams(location), location.pathname);
};

// Traces & OutboundCall methods
export const linkToTracesForOutboundCall = (
  location,
  organization,
  service,
  tagSet,
  type
) => {
  const path = servicePath(organization, service) + `/traces/${type}`;
  const search = { tag_set: JSON.stringify(tagSet) };
  return updateLocation(withoutSort(location), path, search);
};

export const linkToTransaction = (
  location,
  organization,
  service,
  transactionId
) => {
  const path =
    servicePath(organization, service) + `/transactions/${transactionId}`;
  return updateLocation(location, path);
};

export const linkToExceptions = (location, organization, service) => {
  const path = servicePath(organization, service) + `/exceptions`;
  return updateLocation(location, path);
};

export const linkToExceptionsTabs = (
  location,
  organization,
  service,
  exceptionId,
  currentTab
) => {
  const path =
    servicePath(organization, service) + `/exceptions/${exceptionId}`;
  const tabName =
    currentTab === exceptionsTabEnum.BACKTRACES.value ? `/${currentTab}` : "";
  return updateLocation(location, path + tabName);
};

export const linkToExceptionMessage = (location, organization, service, id) => {
  const path = servicePath(organization, service) + `/exceptions/${id}`;
  return updateLocation(location, path);
};

export const linkToServiceList = (location, organization) => {
  const path = `/apm/${organization}/services/list`;
  return updateLocation(location, path);
};

export const linkToServicesTab = (location, organization, tab) => {
  const path = `/apm/${organization}/services/${tab}`;
  return updateLocation(location, path);
};

export const linkToService = (location, organization, service, fromTrace) => {
  const path = servicePath(organization, service);

  if (fromTrace) {
    updateLocation(withoutSpanMultiSelectWithOrigin(location), path);
  }
  return updateLocation(withoutSort(location), path);
};

export const linkToServiceTab = (location, organization, service, tabName) => {
  const path = servicePath(organization, service);

  if (!tabName) {
    return updateLocation(withoutTracedRequestsFilters(location), path);
  }

  return updateLocation(
    withoutTracedRequestsFilters(location),
    `${path}/${tabName}`
  );
};

export const linkToTracePanelTab = (
  location,
  organization,
  service,
  traceId,
  spanId,
  tabName
) => {
  const path =
    servicePath(organization, service) + `/traces/${traceId}/span/${spanId}`;
  const name = tabName ? `/${tabName}` : "";
  return updateLocation(location, path + name);
};

export const linkViewAll = (location, organization, service, type) => {
  const path = servicePath(organization, service) + `/${type}`;

  return updateLocation(location, path);
};

export const linkToTraceRequests = (location, organization, service) => {
  const path = servicePath(organization, service) + `/traces`;
  return updateLocation(withoutSort(location), path);
};

export const linkToTraceExceptions = (location, organization, service) => {
  const path = servicePath(organization, service) + `/traces/exceptions`;
  return updateLocation(withoutSort(location), path);
};

export const linkToTraceSpanSummary = (
  organization,
  service,
  traceId,
  spanId,
  location
) => {
  const path = linkToTraceSummary(organization, service, traceId);
  const searchParams = withoutSpanMultiSelectWithOrigin(location).search || "";

  if (spanId) {
    const originSpanId = areSearchParamsSet(location)
      ? `${spanId}${withoutSpanMultiSelectWithOrigin(location).search}`
      : `${spanId}`;

    return `${path}/${originSpanId}`;
  }

  return path + searchParams;
};

export const linkToTraceSpanDetails = (
  organization,
  service,
  traceId,
  spanId,
  location
) => {
  const path = linkToTraceDetails(organization, service, traceId);
  const searchParams = withoutSpanMultiSelectWithOrigin(location).search || "";

  if (spanId) {
    const originSpanId = areSearchParamsSet(location)
      ? `${spanId}${withoutSpanMultiSelectWithOrigin(location).search}`
      : `${spanId}`;
    return `${path}/${originSpanId}`;
  }

  return path + searchParams;
};

export const linkToTraceDetails = (organization, service, traceId) => {
  return servicePath(organization, service) + `/traces/${traceId}`;
};

export const linkToTraceSummary = (organization, service, traceId) =>
  linkToTraceDetails(organization, service, traceId) + "/summary";

export const linkToTraceSpan = (
  organization,
  service,
  traceId,
  spanId,
  location
) => {
  const path = linkToTraceDetails(organization, service, traceId);
  const loc = withoutTracedRequestsFilters(location);

  if (!flagOnOrDevelopment(ROLLOUT_TRACE_LEVEL_ROOT_CAUSE)) {
    return path;
  }

  return path + "/span/" + spanId + loc.search;
};

const createSelectionParams = (selectionParam, type, includes) => {
  const queryParams = [
    `${SELECTION_PARAM}=${idFor(selectionParam)}`,
    `${SELECTION_INCLUDE_PARAM}=${includes}`,
    `${SELECTION_TYPE_PARAM}=${type}`,
  ].join("&");

  return queryParams;
};

const returnSearchParamsWithLocation = (queryParams, loc, originSpan) => {
  const params = originSpan
    ? `${queryParams}&${ORIGIN_SPAN}=${originSpan}`
    : queryParams;

  return areSearchParamsSet(loc) ? `${loc.search}&${params}` : `?${params}`;
};

export const linkToTraceDetailsWithSelection = (
  organization,
  service,
  traceId,
  selectionParam,
  location,
  type = "errors",
  includes = "all",
  originSpan
) => {
  const path = linkToTraceDetails(organization, service, traceId);
  const loc = withoutTracedRequestsFilters(location);
  const queryParams = createSelectionParams(selectionParam, type, includes);

  if (!flagOnOrDevelopment(ROLLOUT_TRACE_LEVEL_ROOT_CAUSE)) {
    return path;
  }

  return path + returnSearchParamsWithLocation(queryParams, loc, originSpan);
};

export const linkToTraceDetailsWithSelectedSpanId = (
  organization,
  service,
  traceId,
  spanId
) => {
  const path = linkToTraceDetails(organization, service, traceId);
  const loc = withoutTracedRequestsFilters(location);

  if (spanId) {
    const params = `${ORIGIN_SPAN}=${spanId}`;
    const query = areSearchParamsSet(loc)
      ? `${loc.search}&${params}`
      : `?${params}`;
    return `${path}/span/${spanId}` + query;
  }
  const query = areSearchParamsSet(loc) ? `${loc.search}` : ``;
  return `${path}` + query;
};

export const linkToTraceDetailsWithSelectedLayer = (
  match,
  location,
  traceId,
  originSpan,
  spanId,
  type,
  count
) => {
  const service = get(match, "params.service");
  const organization = get(match, "params.organization");

  const path = linkToTraceDetails(organization, service, traceId);
  const loc = withoutTracedRequestsFilters(location);

  if (count > 1) {
    const queryParams = [
      `${SELECTION_PARAM}=${type}`,
      `${SELECTION_TYPE_PARAM}=${"layer"}`,
      `${SELECTION_INCLUDE_PARAM}=${"all"}`,
    ].join("&");

    return path + returnSearchParamsWithLocation(queryParams, loc, originSpan);
  } else {
    const queryParams = [
      `${SELECTION_INCLUDE_PARAM}=${"layer"}`,
      `${SELECTION_TYPE_PARAM}=${"type"}`,
    ].join("&");

    return (
      `${path}/span/${spanId}` +
      returnSearchParamsWithLocation(queryParams, loc)
    );
  }
};

export const linkToTraceTransaction = (
  location,
  organization,
  service,
  transactionId
) => {
  return getSearchParamsForPath(
    location,
    null,
    servicePath(organization, service) + `/transactions/${transactionId}`
  );
};

export const linkToTraceTransactionWithEndTime = (
  organization,
  service,
  transaction,
  endTime
) => {
  return withDurationAndEndTime(
    servicePath(organization, service) + `/transactions/${transaction}`,
    endTime
  );
};

export const linkToTraceService = (organization, service, endTime) => {
  return withDurationAndEndTime(servicePath(organization, service), endTime);
};

export const linkToProblemDataType = (
  organizationId,
  location,
  problemType
) => {
  return updateLocation(
    location,
    `/apm/${organizationId}/services/${problemType}`
  );
};

export const linkToTransactionsService = (
  organizationId,
  location,
  service
) => {
  return updateLocation(
    location,
    `/apm/${organizationId}/services/${service}/transactions`
  );
};

export const linkToRemoteServices = (organizationId, location, service) => {
  return updateLocation(
    location,
    `/apm/${organizationId}/services/${service}/remote`
  );
};

export const linkToDatabasesServices = (organizationId, location, service) => {
  return updateLocation(
    location,
    `/apm/${organizationId}/services/${service}/databases`
  );
};

export const linkToCachesServices = (organizationId, location, service) => {
  return updateLocation(
    location,
    `/apm/${organizationId}/services/${service}/caches`
  );
};
export const linkToOverviewService = (organizationId, location, service) => {
  return updateLocation(location, `/apm/${organizationId}/services/${service}`);
};

export const getSearchParam = location => {
  const { search } = parse(location.search);
  return search;
};

export const linkForPanelMultiSelect = (
  location,
  organization,
  service,
  traceId,
  selection,
  selectionType,
  span
) => {
  let path =
    servicePath(organization, service) +
    `/traces/${traceId}/span/${span.span_id}`;

  let selectionInclude = SELECTION_INCLUDE_ALL;
  switch (selectionType) {
    case SELECTION_TYPE_LAYER: {
      if (span.isAsync) {
        selectionInclude = SELECTION_INCLUDE_ASYNC;
      } else {
        selectionInclude = SELECTION_INCLUDE_SYNC;
      }
      break;
    }
    case SELECTION_TYPE_QUERY: {
      path += "/queries";
      break;
    }
    case SELECTION_TYPE_ERROR: {
      path += "/errors";
      break;
    }
  }

  return linkForSpanMultiSelect(
    location,
    path,
    selectionType,
    selection,
    selectionInclude
  );
};

export const linkForSpanMultiSelect = (
  location,
  path,
  selectionType,
  selection,
  selectionInclude
) => {
  let search = {
    [SELECTION_TYPE_PARAM]: selectionType,
    [SELECTION_PARAM]: selection,
  };

  if (selectionInclude) {
    search = Object.assign({}, search, {
      [SELECTION_INCLUDE_PARAM]: selectionInclude,
    });
  }

  return updateLocation(withoutSelectionInclude(location), path, search);
};

export const tracePath = (id, match, originService, spanId) => {
  const service = get(match, "params.service");
  const organization = get(match, "params.organization");

  const path = linkToTraceDetails(organization, service, id);

  if (!flagOnOrDevelopment(ROLLOUT_TRACE_LEVEL_ROOT_CAUSE)) {
    return path;
  }

  if (!spanId || originService === service) {
    return path + "/summary";
  }

  return `${path}/summary/${spanId}`;
};

export const getTracePathFromTrace = (match, trace) => {
  const { id, originService, spanId } = trace;
  const path = tracePath(id, match, originService, spanId);

  if (!flagOnOrDevelopment(ROLLOUT_TRACE_LEVEL_ROOT_CAUSE)) {
    return path;
  }

  return updateLocation(withoutTracedRequestsFilters(location), path);
};

const getSearchParamsForPath = (location, span, path) => {
  const searchParams = areSearchParamsSet(location)
    ? `${withoutOriginSpan(location).search}&${ORIGIN_SPAN}=${span}`
    : `?${ORIGIN_SPAN}=${span}`;

  const searchWithoutOriginSpan = areSearchParamsSet(location)
    ? location.search
    : "";

  return span ? path + searchParams : path + searchWithoutOriginSpan;
};

export const getClearSelectionPath = (state, props) => {
  const params = get(props, "match.params", {});
  const { service, organization, traceId } = params;
  const span = getOriginSpanId(state, props);
  const path = linkToTraceDetails(organization, service, traceId);

  return getSearchParamsForPath(location, span, path);
};

export const getExtentErrorsPath = (state, props) => {
  const params = get(props, "match.params", {});
  const { service, organization, traceId } = params;
  const base = linkToTraceDetails(organization, service, traceId);
  const span = getOriginSpanId(state, props);

  const errors = get(props, "marker.errors", []);
  const errorsCount = errors.length;
  const error = errors[errorsCount - 1];
  const extent = error.extent.span_id;

  if (errorsCount === 0) {
    return base;
  }

  return getSearchParamsForPath(
    location,
    span,
    `${base}/span/${extent}/errors`
  );
};

export const linkToApmTab = (location, target) => {
  return `${location}/${target}`;
};
